import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { MobileNavigation } from '../components/MobileNavigation';
import { Plus, Tag, Percent, CurrencyDollar, Trash, Calendar, Check, X } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function AdminDiscountCodes() {
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: '',
    min_purchase: 0,
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  
  useEffect(() => {
    loadCodes();
  }, []);
  
  const loadCodes = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/discount-codes`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCodes(data);
      } else {
        toast.error('Failed to load discount codes');
      }
    } catch (error) {
      console.error('Load codes error:', error);
      toast.error('Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code.trim()) {
      toast.error('Please enter a discount code');
      return;
    }
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/discount-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          max_uses: formData.max_uses ? parseInt(formData.max_uses) : null
        })
      });
      
      if (response.ok) {
        const newCode = await response.json();
        setCodes([newCode, ...codes]);
        setShowAddDialog(false);
        setFormData({
          code: '',
          discount_type: 'percentage',
          discount_value: 10,
          max_uses: '',
          min_purchase: 0,
          valid_from: new Date().toISOString().split('T')[0],
          valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        });
        toast.success('Discount code created!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create discount code');
      }
    } catch (error) {
      console.error('Create code error:', error);
      toast.error('Failed to create discount code');
    }
  };
  
  const toggleCodeStatus = async (codeId, currentStatus) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/discount-codes/${codeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ active: !currentStatus })
      });
      
      if (response.ok) {
        setCodes(codes.map(c => 
          c.code_id === codeId ? { ...c, active: !currentStatus } : c
        ));
        toast.success(`Discount code ${!currentStatus ? 'activated' : 'deactivated'}`);
      }
    } catch (error) {
      console.error('Toggle code error:', error);
      toast.error('Failed to update discount code');
    }
  };
  
  const deleteCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) return;
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/discount-codes/${codeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setCodes(codes.filter(c => c.code_id !== codeId));
        toast.success('Discount code deleted');
      }
    } catch (error) {
      console.error('Delete code error:', error);
      toast.error('Failed to delete discount code');
    }
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };
  
  const getCodeStatus = (code) => {
    if (!code.active) return { label: 'Inactive', color: 'bg-gray-500' };
    if (isExpired(code.valid_until)) return { label: 'Expired', color: 'bg-red-500' };
    if (code.max_uses && code.uses_count >= code.max_uses) return { label: 'Maxed Out', color: 'bg-yellow-500' };
    return { label: 'Active', color: 'bg-green-500' };
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-discount-codes">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-heading">DISCOUNT CODES</h1>
              <p className="text-sm text-muted-foreground">
                {codes.length} code{codes.length !== 1 ? 's' : ''} total
              </p>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-code-btn">
                  <Plus size={20} weight="bold" className="mr-2" />
                  Add Code
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">CREATE DISCOUNT CODE</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="code">Code</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                      placeholder="e.g., SUMMER20"
                      className="uppercase"
                      required
                      data-testid="code-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Discount Type</Label>
                      <Select 
                        value={formData.discount_type} 
                        onValueChange={(v) => setFormData({...formData, discount_type: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage (%)</SelectItem>
                          <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Discount Value</Label>
                      <Input
                        type="number"
                        min="0"
                        max={formData.discount_type === 'percentage' ? 100 : 999}
                        value={formData.discount_value}
                        onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value)})}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Max Uses (blank = unlimited)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.max_uses}
                        onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                        placeholder="Unlimited"
                      />
                    </div>
                    <div>
                      <Label>Min Purchase ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.min_purchase}
                        onChange={(e) => setFormData({...formData, min_purchase: parseFloat(e.target.value)})}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Valid From</Label>
                      <Input
                        type="date"
                        value={formData.valid_from}
                        onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Valid Until</Label>
                      <Input
                        type="date"
                        value={formData.valid_until}
                        onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 btn-primary">
                      Create Code
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 py-6">
        {codes.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <Tag size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg mb-2">NO DISCOUNT CODES</h3>
              <p className="text-muted-foreground">
                Create your first discount code to offer promotions.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {codes.map((code) => {
              const status = getCodeStatus(code);
              return (
                <Card key={code.code_id} className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          {code.discount_type === 'percentage' ? (
                            <Percent size={20} className="text-primary" weight="bold" />
                          ) : (
                            <CurrencyDollar size={20} className="text-primary" weight="bold" />
                          )}
                        </div>
                        <div>
                          <p className="font-mono font-bold text-lg">{code.code}</p>
                          <p className="text-sm text-muted-foreground">
                            {code.discount_type === 'percentage' 
                              ? `${code.discount_value}% off`
                              : `$${code.discount_value.toFixed(2)} off`}
                          </p>
                        </div>
                      </div>
                      <Badge className={`${status.color} text-white`}>
                        {status.label}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                      <div>
                        <p className="text-muted-foreground">Uses</p>
                        <p className="font-medium">{code.uses_count} / {code.max_uses || '∞'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Min Purchase</p>
                        <p className="font-medium">${code.min_purchase?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Valid From</p>
                        <p className="font-medium">{formatDate(code.valid_from)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Valid Until</p>
                        <p className="font-medium">{formatDate(code.valid_until)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={code.active}
                          onCheckedChange={() => toggleCodeStatus(code.code_id, code.active)}
                        />
                        <span className="text-sm text-muted-foreground">
                          {code.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteCode(code.code_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        <Trash size={16} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}
